import { StateCreator } from 'zustand';
import Cookies from 'js-cookie';
import getConfig from 'next/config';
import * as authSvc from '@/services/auth.service';
import {
  clearDeferredAuthPendingIdentify,
  clearKlaviyoIdentify,
  ensureKlaviyoAuthSessionUntied,
  klaviyoIdentify,
  klaviyoIdentifyAnonymousSession,
  resumePendingKlaviyoIdentify,
  resumePendingKlaviyoSubscription,
  wasKlaviyoAuthIdentified,
} from '@/services/forms.service';
import { STATUS_OK } from '@/utils/constants';
import { GOOGLE_SIGN_IN, SIGN_UP, BE_LOG_IN } from '@/consts/GA';
import { IAuth } from '../types/IAuth';
import INITIAL_STATE from '../initialState';

const { NEXT_PUBLIC_API_URL: DOMAIN_URL } = getConfig().publicRuntimeConfig;

const createAuthSlice: StateCreator<IAuth> = (set, get) => ({
  user: INITIAL_STATE.user,
  glLocation: INITIAL_STATE.glLocation,
  isShowGoogleSignUpPopUp: false,
  isShowSignInPopUp: false,
  loadUser() {
    return authSvc.getUserCommonInfo().then(({ data: user, headers: { ipcountry } }) => {
      if (user['user_email']) {
        (window as any).dataLayer.push({
          userID: user['user_id'],
          userEmail: user['user_email'],
          userHashedEmail: user['user_hashed_email'],
          userPhoneNumber: user['user_phone_number'],
          userFirstName: user['user_first_name'],
          userLastName: user['user_last_name'],
          hashed_user_email_md5: user['hashed_user_email_md5'],
        });
      }

      if (user['is_authenticated']) {
        if (user['is_3rd_auth_new'] && user['user_email']) {
          set({ isShowGoogleSignUpPopUp: true });
          (window as any).dataLayer.push({
            event: 'trackEvent',
            eventCategory: 'Form Submit',
            eventAction: 'Create Account',
            eventLabel: GOOGLE_SIGN_IN,
            eventInteraction: false,
          });
        }
        if (user['3rd_auth_button_location']) {
          const eventName = user['is_3rd_auth_new'] ? SIGN_UP : BE_LOG_IN;
          (window as any).dataLayer.push({
            event: eventName,
            form_location: user['3rd_auth_button_location'],
            general_event_details: GOOGLE_SIGN_IN,
          });
        }
      }

      // Cookie session to Klaviyo on every page load for non-sales-rep users with a known email.
      // Late consent replay (incl. guest footer/ring-sizer) is handled inside klaviyoIdentify.
      // user_email can exist for anonymous guests (UserSessionData / checkout) — only treat as
      // auth when is_authenticated, so guest profiles are not reset when that email later clears.
      if (user['user_is_sales_rep']) {
        clearKlaviyoIdentify();
      } else {
        // Replay deferred list subscriptions after full-page redirects once consent is known
        resumePendingKlaviyoSubscription();
        if (user['is_authenticated'] && user['user_email']) {
          // resetBefore waits until window.klaviyo is ready so guest→login cannot merge
          // into a prior guest cookie. Auth marker is set only after identify succeeds.
          klaviyoIdentify(
            {
              email: user['user_email'],
              firstName: user['user_first_name'],
              lastName: user['user_last_name'],
            },
            { fromAuth: true, resetBefore: !wasKlaviyoAuthIdentified() }
          );
        } else if (user['user_email']) {
          // Anonymous known email — untie any prior auth session before identifying as guest.
          // Marker / resetBefore stay set until klaviyo.reset actually runs so guest
          // identify cannot merge into the previous authenticated cookie.
          // Prefer a deferred guest form identify over the ambient session email.
          const needsReset = wasKlaviyoAuthIdentified();
          if (needsReset) {
            clearDeferredAuthPendingIdentify();
          }
          klaviyoIdentifyAnonymousSession(
            {
              email: user['user_email'],
              firstName: user['user_first_name'],
              lastName: user['user_last_name'],
            },
            { resetBefore: needsReset }
          );
        } else {
          const needsReset = wasKlaviyoAuthIdentified();
          if (needsReset) {
            // Do not re-apply a deferred authenticated email to the anonymous browser
            clearDeferredAuthPendingIdentify();
          }
          // Force resetBefore when ending auth so a deferred guest identify cannot
          // merge into the prior auth cookie if klaviyo loads after this call.
          // If nothing is pending, keep retrying untie until klaviyo.reset succeeds.
          if (!resumePendingKlaviyoIdentify({ resetBefore: needsReset }) && needsReset) {
            ensureKlaviyoAuthSessionUntied();
          }
        }
      }

      set(() => ({
        user: {
          ...user,
          isAuthenticated: user.is_authenticated,
          hasShowroomHeader: user.add_dropdown_header,
          isPosAdyen: user.pos_permission?.[0]?.includes('POS Terminal - Adyen - ') || false,
          isSalesRep: user.user_is_sales_rep,
          notInternal: user.not_internal,
          userName: user.user_name,
          userEmail: user.user_email,
          isEuropeanUnionMember: user.is_europena_union_member,
          currCountry: user.curr_country,
          currSymbol: user.curr_symbol,
          isGlobalEFF: (user.globalE_flag && user.global_e_site) || false,
          customerCity: user.customer_city,
          customerIp: user.customer_ip,
          convenientShowroomPhoneNumber: user.format_number,
          convenientShowroomFullPhoneNumber: user.full_number,
          currShowroom: user.curr_showroom || 'Other',
          ipCountry: ipcountry,
          ipLatitude: user.ipLatitude ?? 0,
          ipLongitude: user.ipLongitude ?? 0,
          globalEFlag: user.globalE_flag || false,
          globalESite: user.global_e_site || false,
          globalEUKOrAUFlag: user.globalE_uk_or_au_flag || false,
          thirdAuthButtonLocation: user['3rd_auth_button_location'],
        },
      }));

      Cookies.set('be_internal', user.user_is_sales_rep || false);
      return ipcountry;
    });
  },
  login(form) {
    return authSvc.login(form).then(async ({ data }) => {
      if (data.status === STATUS_OK) {
        await get().loadUser();
      }
      return data;
    });
  },
  logout() {
    // Untie Klaviyo in-page (clearKlaviyoIdentify retries until reset succeeds).
    // Do not rely on loadUser — logout never calls it, and guest forms can identify
    // on this same Layout mount before any later navigation.
    clearKlaviyoIdentify();
    ensureKlaviyoAuthSessionUntied();
    const { user: initialUserData, wishlist, cart } = INITIAL_STATE;
    const currentUserData = get().user;
    set(() => ({
      user: { ...initialUserData, isGlobalEFF: currentUserData.isGlobalEFF, globalEFlag: currentUserData.globalEFlag },
      wishlist,
      cart,
    }));
    Cookies.set('wlCount', 0);
    Cookies.remove('wishID', { path: '' });
    Cookies.set('cartCount', 0);
    Cookies.set('be_internal', false);
    return authSvc.logout();
  },
  async register(form) {
    const { data } = await authSvc.register(form);
    if (data.status === STATUS_OK) {
      await get().loadUser();
    }
    return data;
  },
  setGeolocation: async geoLoc => {
    let { latitude, longitude } = geoLoc;

    if (!latitude || !longitude) {
      try {
        const apiUrl = `${DOMAIN_URL}/api/headers/`;

        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }

        const data = await response.json();
        latitude = parseFloat(data.iplatitude || response.headers.get('iplatitude') || '0');
        longitude = parseFloat(data.iplongitude || response.headers.get('iplongitude') || '0');
      } catch (err: any) {
        latitude = null;
        longitude = null;
      }
    }

    set(() => ({
      user: {
        ...get().user,
        ipLatitude: latitude || 0,
        ipLongitude: longitude || 0,
      },
    }));

    return new Promise<{ latitude: number | null; longitude: number | null }>(resolve => {
      resolve({
        latitude: latitude || null,
        longitude: longitude || null,
      });
    });
  },
  setLocation: newLocalization =>
    set(() => ({
      glLocation: {
        ...get().glLocation,
        ...newLocalization,
      },
    })),
  setIsShowGoogleSignUpPopUp(isShowGoogleSignUpPopUp: boolean) {
    set({ isShowGoogleSignUpPopUp });
  },
  setIsShowSignInPopUp(isShowSignInPopUp: boolean) {
    set({ isShowSignInPopUp });
  },
});

export default createAuthSlice;
